import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const OUTFITS = [["golf", "Golf"], ["atelier", "Atelier"], ["gym", "Gym"], ["inner_path", "Inner Path"], ["networker", "Networker"], ["calm", "Calm (Foundations / Lights)"]];
const field = "w-full rounded-lg border border-[#d9d4c7] bg-white px-3 py-2 text-sm";

const download = async (c) => {
  const r = await axios.get(`${API}/api/admin/talking-clips/${c.id}/file`, { ...auth(), responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = `${c.who}-welcome${c.name ? `-${c.name.toLowerCase().replace(/\s+/g, "-")}` : ""}.mp4`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const ClipRow = ({ c }) => (
  <li className="flex items-start justify-between gap-4 border-t border-[#eee9dc] py-3" data-testid={`talking-clip-${c.id}`}>
    <div className="min-w-0">
      <p className="text-sm text-[#2b2b2b]">{c.who === "richard" ? "Richard" : "Adelaida"} · {c.outfit}{c.name ? ` · for ${c.name}` : ""}{c.custom ? " · own words" : " · ready-made"}{c.brand ? ` · ${c.brand}` : ""}</p>
      <p className="text-xs text-[#6b6b6b] mt-1 line-clamp-2">{c.text}</p>
    </div>
    {c.status === "ready" ? (
      <button type="button" onClick={() => download(c)} data-testid={`talking-clip-download-${c.id}`} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#4A6741] text-white px-4 py-2 text-xs"><Download size={13} /> Download</button>
    ) : c.status === "failed" ? <span className="text-xs text-[#a0522d]">Failed</span> : <span className="text-xs text-[#6b6b6b] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Making…</span>}
  </li>
);

export default function AdminTalkingClips() {
  const [f, setF] = useState({ who: "adelaida", outfit: "golf", mode: "ready", name: "", text: "", brand: "reclaim" });
  const [clips, setClips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => axios.get(`${API}/api/admin/talking-clips`, auth()).then((r) => setClips(r.data.clips)).catch((e) => setErr(e.response?.status === 403 ? "Admins only." : ""));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!clips.some((c) => c.status === "rendering")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [clips]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const make = async () => {
    setBusy(true); setErr("");
    try { await axios.post(`${API}/api/admin/talking-clips`, { who: f.who, outfit: f.outfit, name: f.name, brand: f.brand, text: f.mode === "own" ? f.text : "" }, auth()); await load(); }
    catch { setErr("Couldn't start the clip. Please try again."); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#f6f4ee] px-5 py-10" data-testid="admin-talking-clips-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl text-[#2b2b2b]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Talking clips</h1>
        <p className="mt-1 text-sm text-[#6b6b6b]">A short video of Adelaida or Richard saying a welcome line, to share with a client.</p>
        <div className="mt-6 rounded-2xl border border-[#e5e0d3] bg-white p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-[#6b6b6b]">Coach<select value={f.who} onChange={set("who")} className={field} data-testid="talking-clip-who"><option value="adelaida">Adelaida</option><option value="richard">Richard</option></select></label>
            <label className="text-xs text-[#6b6b6b]">Look<select value={f.outfit} onChange={set("outfit")} className={field} data-testid="talking-clip-outfit">{OUTFITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          </div>
          <label className="block text-xs text-[#6b6b6b]">Logo<select value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value, outfit: e.target.value === "soulumo" && f.outfit === "golf" ? "calm" : f.outfit })} className={field} data-testid="talking-clip-brand"><option value="reclaim">Reclaim</option><option value="soulumo">Soulumo</option></select></label>
          <label className="block text-xs text-[#6b6b6b]">Client's first name (optional, shown as a soft caption)<input value={f.name} onChange={set("name")} maxLength={40} className={field} data-testid="talking-clip-name" /></label>
          <div className="flex gap-2">
            {[["ready", "Ready-made welcome"], ["own", "Write your own"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setF({ ...f, mode: v })} data-testid={`talking-clip-mode-${v}`}
                className={`rounded-full px-4 py-2 text-xs border ${f.mode === v ? "bg-[#2b2b2b] text-white border-[#2b2b2b]" : "border-[#d9d4c7] text-[#4b4b4b]"}`}>{l}</button>
            ))}
          </div>
          {f.mode === "own" && <textarea value={f.text} onChange={set("text")} maxLength={400} rows={4} placeholder="What should they say? (up to about 30 seconds)" className={field} data-testid="talking-clip-text" />}
          {err && <p className="text-sm text-[#a0522d]" data-testid="talking-clip-error">{err}</p>}
          <button type="button" onClick={make} disabled={busy || (f.mode === "own" && f.text.trim().length < 5)} data-testid="talking-clip-make"
            className="rounded-full bg-[#4A6741] text-white px-6 py-2.5 text-sm disabled:opacity-50 inline-flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Make clip</button>
        </div>
        <ul className="mt-6 rounded-2xl border border-[#e5e0d3] bg-white px-5" data-testid="talking-clips-list">
          {!clips.length ? <li className="py-4 text-sm text-[#6b6b6b]">No clips yet.</li> : clips.map((c) => <ClipRow key={c.id} c={c} />)}
        </ul>
      </div>
    </div>
  );
}
